// src/pages/PlannerEditor.jsx
import { useEffect, useState, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { format, parseISO, eachDayOfInterval } from 'date-fns'

import useMealPlanner from '../hooks/useMealPlanner'
import useRecipe from '../hooks/useRecipe'

import Button from '../components/Shared/Button'
import Spinner from '../components/Shared/Spinner'
import Tag from '../components/Shared/Tag'
import Modal from '../components/Shared/Modal'
import Input from '../components/Shared/Input'
import Dropdown from '../components/Shared/Dropdown'
import toast from 'react-hot-toast'

const MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snack']

export default function PlannerEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    plans,
    activePlan,
    setActivePlan,
    planMeals,
    addEntry,
    removeEntry,
    fetchAll,
  } = useMealPlanner()
  const { recipes } = useRecipe()
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('planner')

  useEffect(() => {
    fetchAll().then(() => setLoading(false))
  }, [fetchAll])

  useEffect(() => {
    const plan = plans.find(p => p.id === parseInt(id))
    if (plan) setActivePlan(plan)
    else toast.error('Plan not found')
  }, [plans, id, setActivePlan])

  const getPlanDays = (start, end) => {
    try {
      const days = eachDayOfInterval({
        start: parseISO(start),
        end: parseISO(end),
      })
      return days.map(d => ({
        label: format(d, 'EEEE'),
        value: format(d, 'EEEE'),
      }))
    } catch {
      return []
    }
  }

  const planDays = activePlan?.start_date && activePlan?.end_date
    ? getPlanDays(activePlan.start_date, activePlan.end_date)
    : []

  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [selectedDay, setSelectedDay] = useState(planDays[0])
  const [selectedMeal, setSelectedMeal] = useState(MEALS[0])
  const [selectedRecipeId, setSelectedRecipeId] = useState(null)
  const [notes, setNotes] = useState('')
  const [pendingRemoveId, setPendingRemoveId] = useState(null)

  const onDragEnd = ({ draggableId, destination }) => {
    if (!destination) return
    const [day, meal_time] = destination.droppableId.split('::')
    addEntry(activePlan.id, {
      recipe_id: Number(draggableId),
      meal_time,
      day,
      notes: '',
    })
  }

  function updateEntryNotes(entryId, notes) {
    const entry = planMeals.find(e => e.id === entryId)
    if (!entry) return
    addEntry(entry.meal_plan_id, {
      recipe_id: entry.recipe_id,
      meal_time: entry.meal_time,
      day: entry.day,
      notes,
    })
  }

  function closeQuickAdd() {
    setQuickAddOpen(false)
    setSelectedDay(planDays[0])
    setSelectedMeal(MEALS[0])
    setSelectedRecipeId(null)
    setNotes('')
  }

  if (loading || !activePlan) {
    return <div className="p-6"><Spinner size="lg" /></div>
  }

  const summaryByDay = planDays.map(d => {
    const entries = planMeals.filter(e => e.day === d.value && e.meal_plan_id === activePlan.id)
    return {
      day: d.label,
      count: entries.length,
      recipes: entries.map(e => recipes.find(r => r.id === e.recipe_id)?.title || 'Recipe')
    }
  })

  return (
    <div className="p-6 space-y-6">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-charcoal">{activePlan.title}</h1>
          <p className="text-muted text-sm">{activePlan.client_tag} — {activePlan.start_date} to {activePlan.end_date}</p>
          <Tag className="mt-2 inline-block text-xs text-muted">
            ID: {activePlan.id}
          </Tag>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            intent="secondary"
            onClick={() => setViewMode(viewMode === 'planner' ? 'summary' : 'planner')}
          >
            {viewMode === 'summary' ? '← Back to Planner' : '🧭 Summary View'}
          </Button>
          <Button intent="secondary" onClick={() => setQuickAddOpen(true)}>+ Quick Add</Button>
          <Button intent="secondary" onClick={() => navigate('/planner')}>← Back to Plans</Button>
        </div>
      </header>

      {viewMode === 'planner' ? (
        <>
          {planMeals.filter(e => e.meal_plan_id === activePlan.id).length === 0 && (
            <div className="text-center text-muted italic py-8">
              🛑 This plan is currently empty. Use Quick Add or drag in recipes to begin.
            </div>
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="w-full overflow-x-auto">
              <div
                className="min-w-[640px] grid gap-[1px] bg-light-gray rounded overflow-hidden text-xs"
                style={{ gridTemplateColumns: `64px repeat(${planDays.length}, 1fr)` }}
              >
                <div className="bg-white sticky left-0 z-10" />
                {planDays.map(d => (
                  <div key={d.value} className="bg-white py-2 text-center font-medium">
                    {d.label}
                  </div>
                ))}

                {MEALS.map(meal => (
                  <Fragment key={meal}>
                    <div className="bg-white py-2 px-1 font-semibold sticky left-0 z-10">{meal}</div>
                    {planDays.map(day => {
                      const cellId = `${day.value}::${meal}`
                      const entries = planMeals?.filter?.(
                        e => e.meal_plan_id === activePlan?.id &&
                             e.day === day.value &&
                             e.meal_time === meal
                      ) || []

                      return (
                        <Droppable key={cellId} droppableId={cellId}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`min-h-[48px] px-1 py-0.5 transition-all duration-200 ${
                                snapshot.isDraggingOver ? 'bg-soft-yellow/40 ring-2 ring-yellow-300' : 'bg-white'
                              }`}
                            >
                              {entries.map((e, idx) => {
                                const r = recipes.find(r => r.id === e.recipe_id)
                                return (
                                  <Draggable
                                    key={e.id ?? `${e.recipe_id}-${idx}`}
                                    draggableId={`${e.recipe_id}`}
                                    index={idx}
                                  >
                                    {dragProvided => (
                                      <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        className="bg-soft-yellow/40 rounded px-1 mb-1 flex justify-between items-center"
                                      >
                                        <div className="flex-1">
                                          <div className="truncate font-medium text-sm">{r?.title ?? 'Recipe'}</div>
                                          <input
                                            type="text"
                                            defaultValue={e.notes}
                                            onBlur={ev => updateEntryNotes(e.id, ev.target.value)}
                                            className="w-full bg-transparent text-xs border-b border-light-gray focus:outline-none text-muted"
                                            placeholder="Add notes..."
                                          />
                                        </div>
                                        <button
                                          onClick={() => {
                                            if (pendingRemoveId) return
                                            setPendingRemoveId(e.id)
                                            removeEntry(e.id, e.meal_plan_id).finally(() => setPendingRemoveId(null))
                                          }}
                                          disabled={pendingRemoveId === e.id}
                                          className={`text-xs ml-1 text-slate-gray hover:text-red-600 ${
                                            pendingRemoveId === e.id ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                          aria-label="remove"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              })}
                              {provided.placeholder}
                              {entries.length === 0 && (
                                <div className="text-center text-xs text-muted italic pt-2">
                                  Drag recipes here
                                </div>
                              )}
                            </div>
                          )}
                        </Droppable>
                      )
                    })}
                  </Fragment>
                ))}
              </div>
            </div>
          </DragDropContext>
        </>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-charcoal">📊 Weekly Summary</h2>
          <ul className="space-y-2">
            {summaryByDay.map(d => (
              <li key={d.day} className="bg-white p-3 rounded shadow-sm">
                <div className="font-medium text-sm text-charcoal">{d.day}</div>
                <div className="text-xs text-muted">{d.count} recipe{d.count !== 1 && 's'}</div>
                <ul className="mt-1 list-disc list-inside text-xs text-muted">
                  {d.recipes.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Modal open={quickAddOpen} title="Quick Add to Meal Plan" onClose={closeQuickAdd}>
          <div className="max-h-[75vh] overflow-y-auto space-y-4 pr-2">
          <Dropdown
            label="Day"
            options={planDays}
            value={selectedDay}
            onChange={setSelectedDay}
          />
          <Dropdown
            label="Meal"
            options={MEALS.map(m => ({ label: m, value: m }))}
            value={selectedMeal}
            onChange={setSelectedMeal}
          />
          <Dropdown
            label="Recipe"
            options={recipes.map(r => ({ label: r.title, value: r.id }))}
            value={selectedRecipeId}
            onChange={setSelectedRecipeId}
          />
          <Input
            label="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button intent="secondary" onClick={closeQuickAdd}>Cancel</Button>
            <Button
              intent="primary"
              onClick={() => {
                if (!activePlan) {
                  toast.error('No active plan selected.')
                  return
                }
                if (!selectedRecipeId) {
                  toast.error('Please select a recipe.')
                  return
                }
                addEntry(activePlan.id, {
                  recipe_id: selectedRecipeId,
                  day: selectedDay.value || selectedDay,
                  meal_time: selectedMeal.value || selectedMeal,
                  notes,
                })
                closeQuickAdd()
              }}
            >
              Add
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}